'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { Home, GraduationCap, CalendarRange, Info, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import sdpsiLogo from '../../../public/images/home/sdpsilogo.png';
import { Button } from '@/components/ui/button';
import { siteConfig } from '@/config/site.config';

const navItems = [
  { name: 'Accueil', url: '/', icon: Home },
  { name: 'Formations', url: '/formations', icon: GraduationCap },
  { name: 'Planning & Inscription', url: '/planning-inscription', icon: CalendarRange },
  { name: 'À propos', url: '/a-propos', icon: Info },
  { name: 'Contact', url: '/contact', icon: Phone }
];

export default function TubelightNavbar() {
  const [activeTab, setActiveTab] = useState('');
  const pathname = usePathname();

  const isItemActive = (url: string) => {
    const path = pathname || '/';
    return path === url || (url !== '/' && path.startsWith(url));
  };

  useEffect(() => {
    const path = pathname || '/';
    const currentTab = navItems.find((item) => path === item.url || (item.url !== '/' && path.startsWith(item.url)));

    if (currentTab) {
      setActiveTab(currentTab.name);
    } else {
      setActiveTab(navItems[0].name);
    }
  }, [pathname]);

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-white/10 bg-[#050B18]/80 backdrop-blur-xl shadow-[0_10px_30px_-18px_rgba(0,0,0,0.65)]">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between lg:h-20">
          <Link href="/" className="relative h-11 w-28 shrink-0 lg:h-12 lg:w-32" aria-label="Retour à l'accueil SDPSI Formation">
            <Image src={sdpsiLogo} alt="SDPSI Formation" fill className="object-contain" sizes="128px" priority />
          </Link>

          <nav aria-label="Navigation principale" className="hidden lg:flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-1.5 shadow-sm">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = isItemActive(item.url);

              return (
                <Link
                  key={item.name}
                  href={item.url}
                  onClick={() => setActiveTab(item.name)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'relative rounded-xl px-3 py-2 text-sm font-semibold transition-all',
                    'text-white/75 hover:bg-white/5 hover:text-accent-200',
                    isActive && 'text-accent-200'
                  )}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <Icon size={16} strokeWidth={2.2} />
                    <span>{item.name}</span>
                  </span>
                  {isActive && (
                    <motion.div
                      layoutId="desktop-nav-active"
                      className="absolute inset-0 rounded-xl bg-gradient-to-r from-accent/15 via-white/5 to-accent/15 ring-1 ring-accent/25"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <Link
              href={`tel:${siteConfig.contact.phoneHref}`}
              className="text-sm font-semibold text-white/80 hover:text-accent-200"
              data-track-event="click_phone"
              data-track-location="navbar"
              data-track-label="navbar-phone"
            >
              {siteConfig.contact.phoneDisplay}
            </Link>
            <Button asChild size="sm" variant="cta">
              <Link href="/contact">Demander un devis</Link>
            </Button>
          </div>

          <nav aria-label="Navigation mobile" className="flex items-center gap-1 rounded-xl border border-white/10 bg-white/5 p-1 lg:hidden">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.name;

              return (
                <Link
                  key={item.name}
                  href={item.url}
                  onClick={() => setActiveTab(item.name)}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={item.name}
                  className={cn(
                    'relative rounded-lg p-2.5 transition-all',
                    'text-white/70 hover:bg-white/5 hover:text-accent-200',
                    isActive && 'text-accent-200'
                  )}
                >
                  <Icon size={18} strokeWidth={2.3} />
                  {isActive && <span className="absolute inset-x-1 bottom-0 h-0.5 rounded-full bg-accent" />}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </header>
  );
}
